import { useEffect, useMemo, useState, useCallback } from "react";

import { css } from "@compiled/react";

import { LinkIconButton } from "@atlaskit/button-next/new";
import LinkExternalIcon from "@atlaskit/icon/core/link-external";
import { token } from "@atlaskit/tokens";

import { NotificationGroup as NotificationGroupType } from "../../types";
import { useWacNotifications } from "../hooks/use-wac-notifications";

import NotificationError from "./notification-error";
import NotificationFeed from "./notification-feed";
import NotificationSkeleton from "./notification-skeleton";
import { groupNotificationsByTime } from "./notifications-time-grouping";

interface NotificationsDrawerProps {
    notificationLogUrl: string;
}

const drawerContainerStyles = css({
    height: "calc(-208px + 100vh)",
    minHeight: "287px",
    width: "540px",
    maxWidth: "100vw",
    overflowY: "auto"
});

const headerContainerStyles = css({
    "display": "flex",
    "justifyContent": "space-between",
    "alignContent": "center",
    "padding": "24px 16px 16px 24px",

    "&::after": {
        content: "''",
        position: "absolute",
        insetBlockEnd: 0,
        insetInlineStart: token("space.300"),
        insetInlineEnd: token("space.300"),
        height: "2px",
        backgroundColor: token("color.border")
    }
});

const headerStyles = css({
    fontFamily:
        "'Atlassian Sans', ui-sans-serif, -apple-system, 'system-ui', 'Segoe UI', Ubuntu, 'Helvetica Neue', sans-serif",
    margin: 0,
    position: "relative"
});

const NotificationsDrawer = ({
    notificationLogUrl
}: NotificationsDrawerProps) => {
    const [groups, setGroups] = useState<NotificationGroupType[]>([]);
    const [loadingGroupId, setLoadingGroupId] = useState<string | null>(null);
    const [continuationToken, setContinuationToken] = useState<string | null>(
        null
    );
    const [hasMoreNotifications, setHasMoreNotifications] = useState(true);
    const { isLoading, error, fetchNotifications } = useWacNotifications();

    useEffect(() => {
        const loadNotifications = async () => {
            const initialGroups = await fetchNotifications({
                notificationLogUrl
            });
            setGroups(initialGroups as NotificationGroupType[]);

            // Extract continuation token from the response
            const token = (initialGroups as unknown as Record<string, unknown>)[
                "__continuationToken"
            ] as string | undefined;
            if (token) {
                setContinuationToken(token);
            }
        };

        loadNotifications();
    }, [notificationLogUrl, fetchNotifications]);

    const handleExpand = useCallback(
        async (groupId: string, continuationToken?: string) => {
            // Check if notifications have already been fetched for this group
            const group = groups.find((g) => g.id === groupId);
            const hasAlreadyFetched = group && group.notifications.length > 1;

            // If already fetched, skip the API call
            if (hasAlreadyFetched) {
                return;
            }

            setLoadingGroupId(groupId);

            const newGroups = await fetchNotifications({
                notificationLogUrl,
                groupId,
                continuationToken
            });

            setGroups((prevGroups) => {
                const groupIndex = prevGroups.findIndex(
                    (g) => g.id === groupId
                );

                if (groupIndex === -1) {
                    return [
                        ...prevGroups,
                        ...(newGroups as NotificationGroupType[])
                    ] as NotificationGroupType[];
                }

                const updatedGroups = [...prevGroups];
                const existingGroup = updatedGroups[groupIndex];
                if (!existingGroup) {
                    return prevGroups;
                }
                const newNotifications = newGroups[0]?.notifications || [];

                updatedGroups[groupIndex] = {
                    ...existingGroup,
                    notifications: [
                        ...existingGroup.notifications,
                        ...newNotifications
                    ],
                    notificationContinuationToken:
                        newGroups[0]?.notificationContinuationToken || ""
                } as NotificationGroupType;

                return updatedGroups;
            });

            setLoadingGroupId(null);
        },
        [notificationLogUrl, fetchNotifications, groups]
    );

    const handleLoadMore = useCallback(async () => {
        if (!continuationToken || isLoading) {
            return;
        }

        const newGroups = await fetchNotifications({
            notificationLogUrl,
            continuationToken
        });

        if (!newGroups || newGroups.length === 0) {
            setHasMoreNotifications(false);
            return;
        }

        const newToken = (newGroups as unknown as Record<string, unknown>)[
            "__continuationToken"
        ] as string | undefined;

        setGroups((prevGroups) => [
            ...prevGroups,
            ...(newGroups as NotificationGroupType[])
        ]);

        if (newToken) {
            setContinuationToken(newToken);
        } else {
            setHasMoreNotifications(false);
        }
    }, [notificationLogUrl, fetchNotifications, continuationToken, isLoading]);

    const groupedNotifications = useMemo(() => {
        // Group notification groups by time, preserving group metadata (additionalActors)
        return groupNotificationsByTime(groups);
    }, [groups]);

    const renderContent = () => {
        if (isLoading && groups.length === 0) {
            return (
                <div>
                    {[...Array(5)].map((_, index) => (
                        <NotificationSkeleton key={index} />
                    ))}
                </div>
            );
        }

        if (error) {
            return <NotificationError />;
        }

        if (groups.length === 0) {
            return <div>No notifications</div>;
        }

        return (
            <NotificationFeed
                groupedNotifications={groupedNotifications}
                onExpand={handleExpand}
                notificationLogUrl={notificationLogUrl}
                loadingGroupId={loadingGroupId}
                onLoadMore={handleLoadMore}
                isLoadingMore={isLoading}
                hasMoreNotifications={hasMoreNotifications}
            />
        );
    };

    return (
        <div css={drawerContainerStyles}>
            <div css={headerContainerStyles}>
                <h2 css={headerStyles}>Notifications</h2>
                <LinkIconButton
                    icon={LinkExternalIcon}
                    href="https://home.atlassian.com/notifications"
                    appearance="subtle"
                    label="Open in a new tab"
                    target="_blank"
                    rel="noopener noreferrer"
                />
            </div>
            {renderContent()}
        </div>
    );
};

export { NotificationsDrawer };
